import {
  ref,
  onMounted,
  defineComponent,
  computed,
} from "@vue/composition-api";

import LoadingAnimation from "@/components/LoadingAnimation/LoadingAnimation.vue";
import FormErrors from "@/components/FormErrors/FormErrors.vue";
import { NamedRts } from "@/router";

import { TOAST_OPTIONS } from "@/constants";

import AutocompleteId from "@/components/Autocomplete/AutocompleteId.vue";
import { AutocompleteResult } from "@/components/Autocomplete/autocomplete-id";

import { inventoryLotsController } from "@/controllers/inventoryLots";
import {
  buildFormInventoryLot,
  prettifyInventoryLotStatus,
  InventoryLot,
} from "@/models/InventoryLot";
import { v4 as uuidv4 } from "uuid";

import {
  prettifyWeightQuantityPriceStatus,
  buildFormWeightQuantityPriceStatus,
} from "@/models/WeightQuantityPriceStatus";

import { buildFormCommodityAutocomplete } from "@/models/Commodity";
import { buildFormClientAutocomplete } from "@/models/Client";
import { buildFormLotUnitAutocomplete } from "@/models/LotUnit";

import { buildSubmitButton, buildFormErrors } from "@/utilities/form-helpers";

import { JSConn } from "@/utilities/js-conn";

export default defineComponent({
  name: "inventoryLotEditForm",
  components: {
    LoadingAnimation,
    AutocompleteId,
    FormErrors,
  },
  props: {
    inventoryLotId: {
      type: String,
      required: true,
    },
  },

  setup(props, ctx) {
    let $store = ctx.root.$store;

    let dataLoaded = ref(false);
    let loadingMessage = ref("Preparing the form...");

    let firstInput: any = ref(null);

    let deleteModal: any = ref(null);

    const showDeleteModal = () => {
      deleteModal.value.show();
    };

    const hideDeleteModal = () => {
      deleteModal.value.hide();
    };

    const deleteInventoryLot = () => {
      const jsConn = new JSConn();
      jsConn.formParams = {
        id: props.inventoryLotId,
      };
      inventoryLotsController.deleteInventoryLot(jsConn).then(() => {
        ctx.root.$router.push({
          name: NamedRts.inventoryLots,
        });
        return Promise.resolve();
      });
    };

    const cancelParams = () => {
      if (props.inventoryLotId === "new") {
        return {
          name: NamedRts.inventoryLots,
        };
      } else {
        return {
          name: NamedRts.inventoryLotShow,
          params: {
            inventoryLotId: props.inventoryLotId,
          },
        };
      }
    };

    let {
      formInventoryLot,
      inventoryLotStatuses,
      loadInventoryLotStatusesIntoForm,
      loadInventoryLotIntoForm,
    } = buildFormInventoryLot();

    const {
      defaultSellPriceStatuses,
      weightDefaultSellPriceStatuses,

      weightStatuses,
      quantityStatuses,

      purchasePriceStatuses,
      weightPurchasePriceStatuses,

      loadWeightQuantityPriceStatusesIntoForm,
    } = buildFormWeightQuantityPriceStatus();

    loadWeightQuantityPriceStatusesIntoForm(defaultSellPriceStatuses);
    loadWeightQuantityPriceStatusesIntoForm(weightDefaultSellPriceStatuses);
    loadWeightQuantityPriceStatusesIntoForm(weightStatuses);
    loadWeightQuantityPriceStatusesIntoForm(quantityStatuses);
    loadWeightQuantityPriceStatusesIntoForm(weightPurchasePriceStatuses);
    loadWeightQuantityPriceStatusesIntoForm(purchasePriceStatuses);

    let { submitButtonText, submitting } = buildSubmitButton();
    let { hasFormErrors, formErrors } = buildFormErrors();

    let { updateCommodity } = buildFormCommodityAutocomplete();
    let { updateClient } = buildFormClientAutocomplete();
    let { updateLotUnit } = buildFormLotUnitAutocomplete();

    const localUpdateCommodity = (evt: AutocompleteResult) => {
      updateCommodity(evt, formInventoryLot.formCommodity, formInventoryLot);
    };

    const localUpdateClient = (evt: AutocompleteResult) => {
      updateClient(evt, formInventoryLot.formClient, formInventoryLot);
    };

    const localUpdateLotUnit = (evt: AutocompleteResult) => {
      updateLotUnit(
        evt,
        formInventoryLot.formLotUnit,
        formInventoryLot,
        "lot_unit_id",
      );
    };

    const localUpdateWeightLotUnit = (evt: AutocompleteResult) => {
      updateLotUnit(
        evt,
        formInventoryLot.formWeightLotUnit,
        formInventoryLot,
        "weight_lot_unit_id",
      );
    };

    const localUpdateAverageWeightLotUnit = (evt: AutocompleteResult) => {
      updateLotUnit(
        evt,
        formInventoryLot.formAverageWeightLotUnit,
        formInventoryLot,
        "average_weight_lot_unit_id",
      );
    };

    const commoditiesForAutocomplete = computed(() => {
      return $store.getters.commoditiesForAutocompleteId;
    });

    const clientsForAutocomplete = computed(() => {
      return $store.getters.clientsForAutocompleteId;
    });

    const lotUnitsForAutocomplete = computed(() => {
      return $store.getters.lotUnitsForAutocompleteId;
    });

    const weightLotUnitsForAutocomplete = computed(() => {
      return $store.getters.weightLotUnitsForAutocompleteId;
    });

    const apiInventoryLot = computed(() => {
      return $store.getters.inventoryLot as InventoryLot;
    });

    onMounted(() => {
      const jsConnCommodities = new JSConn();
      const jsConnClients = new JSConn();
      const jsConnLotUnits = new JSConn();
      const jsConnInventoryLot = new JSConn();

      loadInventoryLotStatusesIntoForm(inventoryLotStatuses);

      let dataPromises = [
        $store.dispatch("needCommodities", jsConnCommodities),
        $store.dispatch("needClients", jsConnClients),
        $store.dispatch("needLotUnits", jsConnLotUnits),
      ];

      if (props.inventoryLotId !== "new") {
        jsConnInventoryLot.formParams = {
          id: props.inventoryLotId,
        };
        dataPromises.push(
          inventoryLotsController.needInventoryLot(jsConnInventoryLot),
        );
      }

      Promise.all(dataPromises)
        .then(() => {
          // if the form is not a new form,
          // then the data needs to be loaded into the store
          if (props.inventoryLotId !== "new") {
            loadInventoryLotIntoForm(apiInventoryLot.value, formInventoryLot);
          }
        })
        .finally(() => {
          dataLoaded.value = true;
          ctx.root.$nextTick(() => {
            if (firstInput.value) {
              firstInput.value.focus();
            }
          });
        });
    });

    const onSubmit = (evt: any) => {
      evt.preventDefault();
      const jsConn = new JSConn();

      hasFormErrors.value = false;
      formErrors.value = [];
      submitting.value = true;
      submitButtonText.value = "Submitting...";

      let apiResponse;
      if (props.inventoryLotId === "new") {
        formInventoryLot.id = uuidv4();
        jsConn.formParams = { ...formInventoryLot };
        apiResponse = inventoryLotsController.createInventoryLot(jsConn);
      } else {
        jsConn.formParams = { ...formInventoryLot };
        apiResponse = inventoryLotsController.updateInventoryLot(jsConn);
      }

      apiResponse
        .then(() => {
          ctx.root.$bvToast.toast(
            "Inventory Lot was successfully saved",
            TOAST_OPTIONS,
          );
          ctx.root.$router.push(cancelParams());
        })
        .catch(() => {
          hasFormErrors.value = true;
          formErrors.value = jsConn.error["response"]["data"];
        })
        .finally(() => {
          submitting.value = false;
          submitButtonText.value = "Submit";
        });
    };

    return {
      dataLoaded,
      loadingMessage,
      formInventoryLot,
      inventoryLotStatuses,
      prettifyInventoryLotStatus,

      commoditiesForAutocomplete,
      localUpdateCommodity,

      clientsForAutocomplete,
      localUpdateClient,

      lotUnitsForAutocomplete,
      weightLotUnitsForAutocomplete,
      localUpdateLotUnit,
      localUpdateWeightLotUnit,
      localUpdateAverageWeightLotUnit,

      onSubmit,
      submitting,
      submitButtonText,

      hasFormErrors,
      formErrors,

      firstInput,
      cancelParams,

      deleteModal,
      showDeleteModal,
      hideDeleteModal,
      deleteInventoryLot,

      defaultSellPriceStatuses,
      weightDefaultSellPriceStatuses,

      weightStatuses,
      quantityStatuses,

      purchasePriceStatuses,
      weightPurchasePriceStatuses,

      prettifyWeightQuantityPriceStatus,
    };
  },
});
